/**
 * @file src/manager.ts
 * @exports manager
 * @summary Coordinates Cognition and vID session state, authorization, bootstrap, routing, and service recovery.
 * @remarks Bootstrap reports whether validated flow setup finished before welcome enables navigation.
 *          Configures identity re-check sessions with consent reset on entry and selfie-only steps.
 *          Preserves an authorized API-key success screen after credential cleanup. Classifies voucher rejections solely by server code.
 *          Invite acceptance analytics cover only the AcceptInvite call; a failed profile refresh afterwards goes to Sentry only.
 *          A failed acceptance reports a bounded reason from the voucher code or the transport error; an invite found already
 *          redeemed before accepting reports `already_accepted`, so every attempt ends in a terminal status.
 *          Identifies the voucher through the analytics helper, which reaches both Mixpanel and PostHog.
 *          Tracks `API Key Unlock Code Sent` around the fire-and-forget send; a failed send reports `delivery_failed`. vID error dialogs fire `User Error Shown`.
 *          A redeemed, expired or canceled voucher at vID start fires `Voucher Rejected`, identified to that voucher.
 */

import { getDefaultStore } from "jotai";
import { focusAtom } from "jotai-optics";
import type { ParsedLocation } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { io } from "socket.io-client";
import axios from "axios";

import {
	allowSampleIdAtom,
	atomWithAsyncFileStorage,
	authCheckAtom,
	currentOrgIdAtom,
	dataStoreAtom,
	defaultVIDStateValues,
	hideSensitiveDataAtom,
	KeyDetails,
	serviceUnavailableAtom,
	VIDState,
	vIDStateName,
} from "./shame/shame-atoms";
import { persistVidPartitionKeyToTabSession, resolveAndPersistVidPartitionKey } from "./views/VID/vidStorageKey";
import ory from "src/helpers/ory/sdk";
import { paths } from "./const";
import {
	analytics,
	cortexApiKeyHeaders,
	createLocalReturnToWithParams,
	getRequestFailReason,
	registerVidHttpApiKeyResolver,
	ANALYTICS,
	type AnalyticsAuthFailReason,
	type AnalyticsInviteFailReason,
	type AnalyticsStatus,
	type AnalyticsUserErrorReason,
	type AnalyticsVoucherRejectReason,
} from "./helpers";
import {
	CheckVoucherValidityQuery,
	DisclosureAddress,
	MeQuery,
	StartVidFlowDocument,
	type StartVidFlowMutation,
	VoucherStatusEnum,
} from "./generated/graphql";
import { sanitizeAddresses } from "./views/VID/views/helpers/sanitizeAddresses";
import { SearchTypeEnum } from "./types";
import { VidStepsEnum } from "./views/VID/const";
import { clearVidDataState } from "./views/VID/views/hooks/clearVidDataShared";
import { getVidSteps } from "./views/VID/views/helpers/getVidSteps";
import { RESET } from "jotai/utils";
import { showDialog } from "@cerebruminc/cerebellum";
import dayjs from "dayjs";
import { getSdk } from "./generated/sdk";
import { fetchDataViaApiKey, isServiceUnavailableError, registerServiceUnavailableHandler } from "./graphql/fetcher";
import { GraphQLRequestError } from "./graphql/GraphQLRequestError";
import { print, type DocumentNode } from "graphql";
import { OrgContactInfo } from "./pages/vid/i/[invite]";
import { env } from "./environment";
import { type AppNavigation, getAppRouter, navigateApp, waitForAppRouter } from "src/lib/app-router";

type RouterQuery = Record<string, string | string[] | undefined>;

const getRouterLocation = (): ParsedLocation => getAppRouter().state.location;

const getRouterPath = (): string => getRouterLocation().pathname;

const getRouterAsPath = (): string => {
	const location = getRouterLocation();
	return `${location.pathname}${location.searchStr ?? ""}${location.hash ? `#${location.hash}` : ""}`;
};

const getRouterParams = (): Record<string, string> => {
	const matches = getAppRouter().state.matches;
	return (matches[matches.length - 1]?.params as Record<string, string>) ?? {};
};

const getRouterSearch = (): RouterQuery => getRouterLocation().search as RouterQuery;

const SERVICE_UNAVAILABLE_RETURN_TO_KEY = "cognition:serviceUnavailableReturnTo";
const WHOAMI_RETRY_ATTEMPTS = 3;
const WHOAMI_RETRY_DELAY_MS = 250;

const isRetryableWhoamiError = (error: unknown): boolean => {
	if (isServiceUnavailableError(error)) {
		return true;
	}

	if (!axios.isAxiosError(error) || error.code === "ERR_CANCELED") {
		return false;
	}

	const status = error.response?.status;
	return status === 401 || status === 403 || (typeof status === "number" && status >= 500);
};

const getLoginSearch = (returnPath: string): Record<string, string> => {
	const url = new URL(`${window.location.origin}${returnPath || ""}`);
	return { refresh: "true", return_to: url.href };
};

const getServiceUnavailableReturnToTarget = (): Pick<AppNavigation, "hash" | "search" | "to"> => {
	if (typeof window === "undefined") {
		return { search: {}, to: paths.home };
	}
	const location = getRouterLocation();
	return {
		...(location.hash ? { hash: location.hash } : {}),
		search: location.search as RouterQuery,
		to: location.pathname || paths.home,
	};
};

type VoucherRejectionStatus = AnalyticsVoucherRejectReason;
const VOUCHER_NOT_FOUND_MESSAGE = "We couldn’t find this invite. Please check your link or contact support.";

// Voucher classification requires the server error codes; message wording is not a contract.
const getVoucherRejection = ({
	error,
}: {
	error: unknown;
}): { rejection: string; status: VoucherRejectionStatus | null } => {
	const code =
		error instanceof GraphQLRequestError
			? error.graphQLErrors.find((entry) => typeof entry.extensions?.code === "string")?.extensions?.code
			: undefined;
	if (typeof code === "string") {
		switch (code) {
			case "VOUCHER_REDEEMED":
				return { rejection: code, status: "redeemed" };
			case "VOUCHER_CANCELED":
				return { rejection: code, status: "canceled" };
			case "VOUCHER_EXPIRED":
				return { rejection: code, status: "expired" };
			default:
				return { rejection: code, status: null };
		}
	}
	return { rejection: "unknown", status: null };
};

// Same server codes as voucher rejection; anything uncoded falls back to the transport classification.
const getInviteFailReason = (error: unknown): AnalyticsInviteFailReason => {
	const { rejection, status } = getVoucherRejection({ error });
	if (status === "redeemed") return "already_accepted";
	if (status || rejection === "VOUCHER_NOT_FOUND") return "voucher_invalid";
	return getRequestFailReason(error);
};

const handleDecodeError = (error: unknown) => {
	Sentry.captureException(error);
	analytics.track(ANALYTICS.userErrorShown, { reason: "invalid_invite_url" satisfies AnalyticsUserErrorReason });
	showDialog({
		autoClose: 30000,
		buttonClick: () => window.open("mailto:support@cerebrum.com", "_blank"),
		buttonText: "Contact Support",
		message: "It appears your invite URL is invalid, as it cannot be decoded. Please contact support.",
		title: "Oh no!",
		tone: "negative",
	});
};

interface AbortablePromise<T> extends Promise<T> {
	abort: () => void;
}

interface AxiosRequesterOptions {
	orgId: string | null;
	apiKey: string;
	headers?: Record<string, string>;
}

interface Invite {
	apiKey?: string;
	apiKeyLocked?: boolean;
	email?: string;
	isUserAlreadyRegistered?: boolean;
	magicLink?: string;
	organization?: OrgContactInfo;
	voucherId?: string;
}

const decodeBase64Utf8 = (value: string): string => {
	if (typeof atob === "function") {
		const binaryString = atob(value);
		const bytes = Uint8Array.from(binaryString, (character) => character.charCodeAt(0));
		return new TextDecoder().decode(bytes);
	}

	return Buffer.from(value, "base64").toString("utf-8");
};

class Manager {
	private store: ReturnType<typeof getDefaultStore>;
	private sdk: ReturnType<typeof getSdk>;
	private socket: ReturnType<typeof io>;
	private socketAuthApiKey = "";

	private _requester = async <TData, TVariables>(
		document: DocumentNode,
		variables?: TVariables,
		options?: AxiosRequesterOptions,
	): Promise<TData> => {
		const orgId = this.getCurrentOrgId();
		const apiKey = this.getApiKey();

		return fetchDataViaApiKey<TData, TVariables>({
			query: print(document),
			variables,
			orgId,
			apiKey,
			options: options?.headers,
		});
	};

	constructor() {
		this.store = getDefaultStore();
		this.sdk = getSdk(this._requester);
		this.socket = io(env.SOCKET_URL, {
			auth: (callback) => {
				const apiKey = this.getApiKey();
				this.socketAuthApiKey = apiKey;
				callback({
					apiKey,
					orgId: this.getCurrentOrgId(),
				});
			},
			withCredentials: true,
			transports: ["websocket"],
		});
		registerVidHttpApiKeyResolver(() => this.getApiKey());
		registerServiceUnavailableHandler((isUnavailable) => this.setServiceUnavailable(isUnavailable));
	}

	public getSocketClient() {
		const apiKey = this.getApiKey();
		if (this.socketAuthApiKey !== apiKey) {
			this.socketAuthApiKey = apiKey;
			this.socket.disconnect().connect();
		}
		return this.socket;
	}

	public getSdk() {
		return this.sdk;
	}

	deriveMessageFromError(e: unknown) {
		const errorMessage =
			// Handle GraphQL errors in response data
			(e as any)?.response?.data?.errors?.[0]?.message ||
			// Handle direct GraphQL errors array
			(e as any)?.errors?.[0]?.message ||
			// Handle REST API error messages from response data
			(e as any)?.response?.data?.message ||
			// Fallback to direct error message
			typeof e === "string"
				? e
				: (e as any)?.message
					? (e as any)?.message
					: "An unknown error occurred";

		// Ensure we always return a string
		return String(errorMessage);
	}

	// Returns true if the error was handled, false otherwise
	// Handled errors are not rethrown and redirect the user to the appropriate page
	// Handles both GraphQL and REST errors
	handleApiKeyError(error: unknown): boolean {
		const errorMessage = this.deriveMessageFromError(error);
		const vidSessionId = this.getVidSessionId();
		const isLocked =
			(axios.isAxiosError(error) && error.response?.data?.message === "API_KEY_LOCKED") ||
			errorMessage.includes("API_KEY_LOCKED");
		const isExpired =
			(axios.isAxiosError(error) && error.response?.data?.message === "API_KEY_EXPIRED") ||
			errorMessage.includes("API_KEY_EXPIRED");

		// If the API key is locked, send the user to the vid/verify-email page
		if (isLocked) {
			const keyDetails = this.getSessionValue(vIDStateName.KeyDetails);
			analytics.track(ANALYTICS.apiKeyUnlockCodeSent, { status: "attempted" satisfies AnalyticsStatus });
			void this.sdk
				.sendApiKeyUnlockVerificationEmail({
					apiKey: keyDetails?.apiKey as string,
				})
				.then(() => {
					analytics.track(ANALYTICS.apiKeyUnlockCodeSent, { status: "succeeded" satisfies AnalyticsStatus });
				})
				.catch((error: unknown) => {
					Sentry.captureException(error);
					analytics.track(ANALYTICS.apiKeyUnlockCodeSent, {
						reason: "delivery_failed" satisfies AnalyticsAuthFailReason,
						status: "failed" satisfies AnalyticsStatus,
					});
				});
			void navigateApp({ search: vidSessionId ? { vidSessionId } : {}, to: paths.vidVerifyEmail });
			return true;
		}

		if (isExpired) {
			// Don't redirect if user is already on the success page
			if (getRouterPath() !== paths.vidSuccess) {
				void navigateApp({
					search: {
						...(vidSessionId ? { vidSessionId } : {}),
						status: "expired",
					},
					to: paths.vidExpired,
				});
			}
			return true;
		}

		return false;
	}

	// Keeps the Sentry voucher tag and user identity in sync; an absent voucher clears both
	// so a new route voucher never inherits the previous voucher's reporting identity.
	private setVoucherReportingIdentity({
		email,
		voucherId,
	}: {
		email?: string | null;
		voucherId?: string | null;
	}): void {
		Sentry.setTag("voucher_id", voucherId || undefined);
		Sentry.setUser(email ? { email } : null);
	}

	// A rejected voucher never reaches the flow's own identify, so identify here or the event has no voucher profile.
	private trackVoucherRejected({
		reason,
		voucherId,
	}: {
		reason: AnalyticsVoucherRejectReason;
		voucherId?: string | null;
	}): void {
		if (voucherId) {
			analytics.identify({ voucherId });
		}
		analytics.track(ANALYTICS.voucherRejected, { reason });
	}

	private captureVoucherCheckError({
		error,
		extra = {},
		flow,
		voucherId,
	}: {
		error: unknown;
		extra?: Record<string, unknown>;
		flow?: string;
		voucherId?: string | null;
	}): void {
		const { rejection } = getVoucherRejection({ error });
		Sentry.captureException(error, {
			tags: {
				...(flow ? { flow } : {}),
				operation: "CheckVoucherValidity",
				voucher_id: voucherId ?? "unknown",
			},
			extra: {
				cachedVoucherStatus: this.getSessionValue(vIDStateName.VidVoucher)?.status ?? null,
				...extra,
				rejection,
			},
		});
	}

	async authorize() {
		// The confirmation screen clears API-key credentials on mount. Rechecking
		// whoami afterward would turn successful submission into an outage.
		if (getRouterPath() === paths.vidSuccess && getRouterSearch().vidSessionId && this.store.get(authCheckAtom)) {
			return;
		}

		let user: MeQuery["me"] | null = null;

		try {
			user = await this.fetchUser();
		} catch (error) {
			// Check if this is an API key error, and if so, handle it
			if (this.handleApiKeyError(error)) {
				return;
			}

			if (!isRetryableWhoamiError(error)) {
				throw error;
			}
		}

		await waitForAppRouter();

		// The vidSessionId is only in the search params if we are authenticating via API key
		if (getRouterSearch().vidSessionId) {
			if (!user) {
				this.setServiceUnavailable(true);
				return;
			}
			this.store.set(authCheckAtom, true);
			this.store.set(currentOrgIdAtom, user?.selectedOrganizationId || null);

			return;
		}

		const authCheckComplete = this.store.get(authCheckAtom);

		if (!authCheckComplete) {
			try {
				const { data: session } = await ory.toSession();

				if (!session) {
					throw new Error("User is not logged in");
				}

				if (!user) {
					try {
						user = await this.fetchUserWithRetry();
					} catch (whoamiError) {
						this.handleUnresolvedWhoamiAfterSession(whoamiError);
						return;
					}
				}

				const hasUserRoleOnly = user.roleAssignment.every((roleAssignment) => roleAssignment.roleV2.name === "USER");

				if (hasUserRoleOnly) {
					const authorizedPathsForUsers = [
						paths.dispute,
						paths.pass,
						paths.postRegistration,
						paths.profileSettings,
						paths.user,
						paths.vid,
						paths.vidRemoteIdUpload,
					];
					const isInAuthorizedPath = authorizedPathsForUsers.some(
						(path) => getRouterPath() === path || getRouterPath().startsWith(`${path}/`), // Exact or subpath match
					);
					if (!isInAuthorizedPath) {
						return navigateApp({ to: paths.notFound });
					}
				}

				// check whether primary email is verified
				const emailObject = session?.identity?.verifiable_addresses?.find((item) => item.via === "email");
				const email = emailObject?.value;
				const isEmailVerified = emailObject?.verified;
				if (!isEmailVerified) {
					// if email is not verified then force redirect user to verification page
					const returnToUrl = createLocalReturnToWithParams(getRouterAsPath());
					// redirect to verification page with return to url as current page
					void navigateApp({
						search: {
							return_to: returnToUrl,
							email,
						},
						to: paths.verification,
					});
					return;
				}

				this.store.set(authCheckAtom, true);
				this.store.set(currentOrgIdAtom, user?.selectedOrganizationId || null);
			} catch (error) {
				console.error(error);
				this.store.set(currentOrgIdAtom, null);
				const currentUrl = getRouterAsPath();

				// route dispute center users to dispute auth page
				// Note that users will go to the 404 page if the email is undefined
				if (currentUrl.startsWith(paths.dispute)) {
					const { orderId, orgName } = getRouterParams();
					const { email } = getRouterSearch();
					void navigateApp({
						params: { orderId, orgName },
						search: { email },
						to: "/dispute/$orgName/auth/$orderId",
					});
					return;
				}
				void navigateApp({ search: getLoginSearch(currentUrl), to: paths.login });
			}
		}
	}

	verifyEmail(): AbortablePromise<boolean> {
		let cancelled = false;

		const _verify = async (): Promise<boolean> => {
			if (cancelled) {
				return false;
			}

			const response = await this.sdk.checkEmailVerified();

			const isVerified = response?.checkEmailVerified?.verified ?? false;

			if (isVerified) {
				return true;
			}

			// If the email is not verified, wait 5 seconds and try again
			await new Promise((resolve) => setTimeout(resolve, 5000));

			return _verify();
		};

		const verifyEmail = _verify() as AbortablePromise<boolean>;

		verifyEmail.abort = () => {
			cancelled = true;
		};

		return verifyEmail;
	}

	getCurrentOrgId() {
		return this.store.get(currentOrgIdAtom);
	}

	/**
	 * Marks the backend as unavailable/available and navigates to/from the outage screen.
	 * Called from the GraphQL client after retries are exhausted, and from the outage UI on retry.
	 *
	 * Navigation from the outage page back to other pagesis keyed off the URL, not `serviceUnavailableAtom`.
	 * The atom is in-memory and resets on refresh while the user is still on this route.
	 */
	setServiceUnavailable(isUnavailable: boolean): void {
		this.store.set(serviceUnavailableAtom, isUnavailable);

		if (typeof window === "undefined") {
			return;
		}

		if (isUnavailable) {
			if (window.location.pathname !== paths.vidServiceUnavailable) {
				sessionStorage.setItem(
					SERVICE_UNAVAILABLE_RETURN_TO_KEY,
					JSON.stringify(getServiceUnavailableReturnToTarget()),
				);
				void navigateApp({ replace: true, to: paths.vidServiceUnavailable });
			}
			return;
		}

		if (window.location.pathname === paths.vidServiceUnavailable) {
			const returnTo = sessionStorage.getItem(SERVICE_UNAVAILABLE_RETURN_TO_KEY);
			sessionStorage.removeItem(SERVICE_UNAVAILABLE_RETURN_TO_KEY);
			void navigateApp({ ...(returnTo ? JSON.parse(returnTo) : { search: {}, to: paths.home }), replace: true });
		}
	}

	/** Clears the outage flag and returns the user to the page they were on. */
	retryAfterServiceUnavailable(): void {
		this.setServiceUnavailable(false);
	}

	getApiKey() {
		const userSession = this.getSessionValue(vIDStateName.KeyDetails);
		const { apiKey } = userSession;
		return apiKey;
	}

	private handleUnresolvedWhoamiAfterSession(error: unknown): void {
		const details = {
			message: error instanceof Error ? error.message : String(error),
			pathname: getRouterPath(),
			status: axios.isAxiosError(error) ? (error.response?.status ?? null) : null,
		};
		console.error("[authorize] /user/whoami failed after valid Ory session", details);
		Sentry.captureMessage("[authorize] /user/whoami failed after valid Ory session", {
			extra: details,
			level: "error",
		});
		this.setServiceUnavailable(true);
	}

	private async fetchUserWithRetry(): Promise<NonNullable<MeQuery["me"]>> {
		let lastError: unknown;

		for (let attempt = 1; attempt <= WHOAMI_RETRY_ATTEMPTS; attempt++) {
			try {
				const user = await this.fetchUser();
				if (user) {
					return user;
				}
				lastError = new Error("User resolution returned no identity");
			} catch (error) {
				if (!isRetryableWhoamiError(error)) {
					throw error;
				}
				lastError = error;
			}

			if (attempt < WHOAMI_RETRY_ATTEMPTS) {
				await new Promise((resolve) => setTimeout(resolve, WHOAMI_RETRY_DELAY_MS * attempt));
			}
		}

		throw lastError;
	}

	/**
	 * Fetches the current user via REST endpoint (not GraphQL).
	 * Works with all forms of authentication, including restricted API keys.
	 *
	 * @throws {Error} If the request fails or authentication is invalid
	 * @returns Promise resolving to user data
	 */
	async fetchUser(): Promise<MeQuery["me"]> {
		const orgId = this.getCurrentOrgId();
		const apiKey = this.getApiKey();

		const headers: Record<string, string> = {};

		if (orgId) {
			headers["x-cerebrum-org-id"] = orgId;
		}

		Object.assign(headers, cortexApiKeyHeaders(apiKey));

		const { data } = await axios.get(`${env.API_URL}/user/whoami`, {
			withCredentials: true,
			headers,
		});

		return data;
	}

	/** @see resolveAndPersistVidPartitionKey in `src/views/VID/vidStorageKey.ts` — must stay aligned with session hooks. */
	getVidSessionId(): string | null {
		return resolveAndPersistVidPartitionKey(getRouterSearch(), getRouterPath());
	}

	getSessionValue<K extends keyof VIDState>(key: K): VIDState[K] {
		const vidSessionId = this.getVidSessionId();
		if (vidSessionId === null) {
			return defaultVIDStateValues[key];
		}
		const sessionAtom = focusAtom(dataStoreAtom, (optic) => optic.prop(vidSessionId).valueOr(defaultVIDStateValues));
		const keyAtom = focusAtom(sessionAtom, (optic) => optic.prop(key).valueOr(defaultVIDStateValues[key]));
		const state = this.store.get(keyAtom);

		return state as VIDState[K];
	}

	setSessionValue<K extends keyof VIDState>(
		key: K,
		value: VIDState[K] | ((prev: VIDState[K]) => VIDState[K]) | typeof RESET,
	): void {
		const vidSessionId = this.getVidSessionId();
		if (vidSessionId === null) {
			return;
		}
		const sessionAtom = focusAtom(dataStoreAtom, (optic) =>
			optic.prop(vidSessionId).valueOr(defaultVIDStateValues).prop(key),
		);
		if (value === RESET) {
			this.store.set(sessionAtom, defaultVIDStateValues[key]);
		} else {
			this.store.set(sessionAtom, (prev) => {
				// The first time this is called, prev will be undefined, since we haven't actually initialized the state yet.
				const confirmedPrev = prev ?? defaultVIDStateValues[key];
				return typeof value === "function" ? value(confirmedPrev) : value;
			});
		}
		const apiKey = this.getApiKey();
		if (key === vIDStateName.KeyDetails && this.socketAuthApiKey !== apiKey) {
			this.socketAuthApiKey = apiKey;
			this.socket.disconnect().connect();
		}
	}

	setSessionFileAtom(key: string, newValue: File | null | typeof RESET) {
		const vidSessionId = this.getVidSessionId();
		if (vidSessionId === null) {
			return;
		}
		// Create (or reference) the atom that stores this file for the current session
		const fileAtom = atomWithAsyncFileStorage<File | null>(key, null, () => vidSessionId);

		try {
			// Handle RESET which clears any stored value
			if (newValue === RESET) {
				console.log("Resetting file storage for key:", key);
				this.store.set(fileAtom, RESET);
				return;
			}

			// Helpful logging when storing a file
			if (newValue instanceof File) {
				console.log("Attempting to store file:", {
					name: newValue.name,
					type: newValue.type,
					size: newValue.size,
					lastModified: new Date(newValue.lastModified).toISOString(),
					key,
					vidSessionId,
				});
			}

			// Store the value (File | null) in the atom
			this.store.set(fileAtom, newValue);

			if (newValue instanceof File) {
				console.log("Successfully stored file:", {
					name: newValue.name,
					type: newValue.type,
					size: newValue.size,
					key,
					vidSessionId,
				});
			} else if (newValue === null) {
				console.log("Successfully cleared file storage for key:", key);
			}
		} catch (error) {
			console.error("Failed to store file in IndexDB:", {
				error,
				fileName: newValue instanceof File ? newValue.name : null,
				fileType: newValue instanceof File ? newValue.type : null,
				fileSize: newValue instanceof File ? newValue.size : null,
				lastModified: newValue instanceof File ? new Date(newValue.lastModified).toISOString() : null,
				key,
				vidSessionId,
				browserInfo: {
					userAgent: window.navigator.userAgent,
					platform: window.navigator.platform,
					vendor: window.navigator.vendor,
				},
			});
			throw error; // Re-throw to maintain original behavior
		}
	}

	clearVidData() {
		const vidSessionId = this.getVidSessionId();
		if (vidSessionId === null) {
			return;
		}

		clearVidDataState({
			resetSessionData: () => {
				this.store.set(dataStoreAtom, (prev) => {
					const { [vidSessionId]: _, ...rest } = prev;
					return rest;
				});
			},
			resetSessionFiles: () => {
				this.setSessionFileAtom("idFrontAtom", null);
				this.setSessionFileAtom("idBackAtom", null);
				this.setSessionFileAtom("selfieAtom", null);
				this.setSessionFileAtom("extractedPhotoFileAtom", null);
			},
			setHideSensitiveData: (value) => this.store.set(hideSensitiveDataAtom, value),
		});
	}

	async getVidBeams() {
		if (this.getSessionValue(vIDStateName.IsIdentityRecheck)) {
			this.setSessionValue(vIDStateName.PassTypeBeamsData, []);
			return;
		}

		const vIdVoucher = this.getSessionValue(vIDStateName.VidVoucher);
		const collectSSN = this.getSessionValue(vIDStateName.CollectSSN);
		const skipLiveness = this.getSessionValue(vIDStateName.SkipLiveness);
		const showDisclosuresStep = this.getSessionValue(vIDStateName.ShowDisclosures);

		// If the voucher has no pass type, there are no beams to load
		if (!vIdVoucher?.passTypeId) {
			this.setSessionValue(vIDStateName.PassTypeBeamsData, []);
			return;
		}

		const response = await this.sdk.BeamList({
			where: {
				passTypes: {
					some: {
						id: { equals: vIdVoucher.passTypeId },
					},
				},
			},
		});

		const beamsArray = response?.beams ?? [];
		// the error can be here
		if (beamsArray.length > 0) {
			// add extra steps
			const additionalSteps = beamsArray.map((beam) => ({
				id: `${VidStepsEnum.AdditionalInformation}/${beam.id}`,
				label: beam.name,
			}));
			const updatedSteps = getVidSteps({
				skipLiveness,
				additionalSteps,
				collectSSN,
				showDisclosuresStep,
			});
			this.setSessionValue(vIDStateName.VidSteps, updatedSteps);
		}

		this.setSessionValue(vIDStateName.PassTypeBeamsData, beamsArray);
	}

	/**
	 * Bootstraps the VID web process by performing several key operations:
	 *
	 * 1. Verifies the user's email to ensure it is valid.
	 * 2. Validates the voucher to check its status and associated invite.
	 * 3. Accepts the invite automatically for the user.
	 * 4. Checks for disclosures and SSN collection requirements.
	 * 5. Loads beams for the pass type and updates the session state.
	 * 6. Records the start of the vID flow.
	 *
	 * This method orchestrates multiple asynchronous operations to prepare the
	 * VID web environment for the user. Returns true only after flow setup finishes.
	 */
	async bootstrapVidWeb(): Promise<boolean> {
		const vidSessionId = this.getVidSessionId();
		if (vidSessionId === null) {
			return false;
		}

		const routerSearch = getRouterSearch();
		const routeVoucherId = Array.isArray(routerSearch.voucherId) ? routerSearch.voucherId[0] : routerSearch.voucherId;
		const hasRouteVoucherId = typeof routeVoucherId === "string" && Boolean(routeVoucherId);
		const sessionVoucherId = this.getSessionValue(vIDStateName.VoucherId);
		const voucherId = hasRouteVoucherId ? routeVoucherId : sessionVoucherId;
		this.setVoucherReportingIdentity({
			email: voucherId === sessionVoucherId ? this.getSessionValue(vIDStateName.VoucherEmail) : null,
			voucherId,
		});
		if (voucherId && voucherId !== sessionVoucherId) {
			this.setSessionValue(vIDStateName.VoucherId, voucherId);
		}
		let vIdVoucher = this.getSessionValue(vIDStateName.VidVoucher);
		if (hasRouteVoucherId) {
			this.setSessionValue(vIDStateName.VidVoucher, RESET);
			vIdVoucher = null;
		}
		const isRemoteCaptureDevice = this.getSessionValue(vIDStateName.IsRemoteCaptureDevice);
		const voucherRouteQuery = {
			...(vidSessionId ? { vidSessionId } : {}),
			...(voucherId ? { voucherId } : {}),
		};

		// Log missing voucherId scenarios for debugging
		if (!voucherId) {
			console.error("BOOTSTRAP_MISSING_VOUCHER_ID: bootstrapVidWeb called without voucherId", {
				timestamp: new Date().toISOString(),
				vidSessionId,
				isRemoteCaptureDevice,
				vIdVoucher: vIdVoucher ? { id: vIdVoucher.id, status: vIdVoucher.status } : null,
				sessionValues: {
					voucherEmail: this.getSessionValue(vIDStateName.VoucherEmail),
					orgContactInfo: this.getSessionValue(vIDStateName.OrgContactInfo),
					keyDetails: this.getSessionValue(vIDStateName.KeyDetails),
				},
				routerSearch,
				currentUrl: typeof window !== "undefined" ? window.location.href : "N/A",
			});
		}

		// Route vouchers are cleared above so every URL-based recovery is revalidated before cached data is trusted.
		if (!vIdVoucher?.packageSet.searchTypes) {
			try {
				const checkVoucherValidityResponse = await this.sdk.CheckVoucherValidity({
					voucherId: voucherId,
				});

				if (!checkVoucherValidityResponse?.checkVoucherValidity?.id) {
					console.error("CheckVoucherValidity returned empty or invalid voucher:", {
						fullResponse: checkVoucherValidityResponse,
						requestedVoucherId: voucherId,
						isRemoteCaptureDevice,
					});
					throw new Error(`Voucher with ID ${voucherId} not found during validity check.`);
				}

				vIdVoucher = checkVoucherValidityResponse.checkVoucherValidity;

				this.setSessionValue(vIDStateName.VidVoucher, vIdVoucher);

				const isExpired = !vIdVoucher?.expiresAt || dayjs(vIdVoucher?.expiresAt).isBefore(dayjs());

				if (isExpired) {
					this.trackVoucherRejected({ reason: "expired", voucherId });
					void navigateApp({ search: { status: "expired", ...voucherRouteQuery }, to: paths.vidExpired });
					return false;
				}
			} catch (e) {
				const errorMessage = this.deriveMessageFromError(e);
				const currentUrl = typeof window !== "undefined" ? window.location.href : "N/A";
				const routerPath = getRouterAsPath() || getRouterPath();

				console.error("CheckVoucherValidity failed:", {
					voucherId,
					timestamp: new Date().toISOString(),
					error: e,
					errorMessage,
					errorType: e?.constructor?.name,
				});

				// Check if this is an API key error, and if so, handle it
				if (this.handleApiKeyError(e)) {
					return false;
				}

				// Known lifecycle rejections are expected states; the tag keeps them
				// filterable from genuine failures in Sentry.
				const { rejection, status: rejectionStatus } = getVoucherRejection({ error: e });

				this.captureVoucherCheckError({
					error: e,
					extra: {
						currentUrl,
						errorMessage,
						isRemoteCaptureDevice: String(isRemoteCaptureDevice),
						routerPath,
					},
					flow: "vid-start",
					voucherId,
				});

				if (rejectionStatus) {
					this.trackVoucherRejected({ reason: rejectionStatus, voucherId });
					void navigateApp({ search: { status: rejectionStatus, ...voucherRouteQuery }, to: paths.vidExpired });
					return false;
				}

				// else show the error in a dialog & they stay on this page. This should rarely happen.
				analytics.track(ANALYTICS.userErrorShown, { reason: "voucher_rejected" satisfies AnalyticsUserErrorReason });
				showDialog({
					buttonClick: () => window.open("mailto:support@cerebrum.com", "_blank"),
					buttonText: "Contact Support",
					id: "unknown-voucher-error",
					message: rejection === "VOUCHER_NOT_FOUND" ? VOUCHER_NOT_FOUND_MESSAGE : errorMessage,
					title: "Oh no!",
					tone: "negative",
				});
				return false;
			}
		}

		analytics.identify({ voucherId });

		// Check if the email is verified
		// TODO: Email verification happens on the /[invite] page, via the
		// `API_KEY_LOCKED` error check. do we need to check this here?
		const emailVerified = await this.verifyEmail();

		if (emailVerified) {
			// vid web base analytics
			const analyticsData = {
				voucherId,
				packageId: vIdVoucher.packageSetId,
				passTypeId: vIdVoucher.passTypeId,
				inviteId: vIdVoucher.inviteId,
				organizationId: vIdVoucher.organizationId,
			};
			analytics.registerSuperProperties(analyticsData);
		}

		// Auto accept invite for user
		await this.acceptInvite();

		// Bootstrap disclosures, SSN collection and steps
		await this.checkForDisclosuresAndSsn(vIdVoucher);

		// Load beams for the pass type
		await this.getVidBeams();
		if (emailVerified) {
			await this.startVidFlow(vIdVoucher);
		}
		return true;
	}

	/** Records the validated voucher's start without interrupting verification on failure. */
	private async startVidFlow(voucher: NonNullable<CheckVoucherValidityQuery["checkVoucherValidity"]>): Promise<void> {
		const { apiKey, apiKeyLocked } = this.getSessionValue(vIDStateName.KeyDetails);
		if (
			voucher.id !== this.getSessionValue(vIDStateName.VoucherId) ||
			apiKeyLocked ||
			voucher.startedAt ||
			voucher.orderId ||
			(voucher.status !== VoucherStatusEnum.Pending && voucher.status !== VoucherStatusEnum.Accepted) ||
			!voucher.expiresAt ||
			dayjs(voucher.expiresAt).isBefore(dayjs())
		) {
			return;
		}

		const vidSessionId = this.getVidSessionId();
		try {
			const { startVidFlow } = await fetchDataViaApiKey<StartVidFlowMutation>({
				apiKey,
				background: true,
				orgId: this.getCurrentOrgId(),
				query: StartVidFlowDocument,
				variables: { voucherId: voucher.id },
			});
			if (this.getVidSessionId() !== vidSessionId || this.getApiKey() !== apiKey || startVidFlow?.id !== voucher.id) {
				return;
			}
			this.setSessionValue(vIDStateName.VidVoucher, (current) =>
				current?.id === voucher.id &&
				(current.status === VoucherStatusEnum.Pending || current.status === VoucherStatusEnum.Accepted)
					? { ...current, ...startVidFlow }
					: current,
			);
		} catch {
			console.warn("Unable to save the vID flow start; it will retry when verification is reopened.");
		}
	}

	async acceptInvite() {
		const vIdVoucher = this.getSessionValue(vIDStateName.VidVoucher);

		if (vIdVoucher?.inviteId && vIdVoucher.status !== VoucherStatusEnum.Accepted) {
			analytics.track(ANALYTICS.inviteAccepted, { status: "attempted" satisfies AnalyticsStatus });

			try {
				if (vIdVoucher.status === VoucherStatusEnum.Started) {
					const { getInvite } = await this.sdk.GetInvite({ inviteId: vIdVoucher.inviteId });
					if (getInvite?.redeemedById || getInvite?.redeemedAt) {
						analytics.track(ANALYTICS.inviteAccepted, {
							reason: "already_accepted" satisfies AnalyticsInviteFailReason,
							status: "failed" satisfies AnalyticsStatus,
						});
						return;
					}
				}
				await this.sdk.AcceptInvite({ inviteId: vIdVoucher.inviteId });

				// updated the status locally, to prevent stale status
				this.setSessionValue(vIDStateName.VidVoucher, (current) =>
					current?.id === vIdVoucher.id && current.status === VoucherStatusEnum.Pending
						? { ...current, status: VoucherStatusEnum.Accepted }
						: current,
				);

				analytics.track(ANALYTICS.inviteAccepted, { status: "succeeded" satisfies AnalyticsStatus });
			} catch (error: unknown) {
				Sentry.captureException(error);
				analytics.track(ANALYTICS.inviteAccepted, {
					reason: getInviteFailReason(error),
					status: "failed" satisfies AnalyticsStatus,
				});
				analytics.track(ANALYTICS.userErrorShown, {
					reason: "invite_accept_failed" satisfies AnalyticsUserErrorReason,
				});
				showDialog({
					buttonClick: () => window.open("mailto:support@cerebrum.com", "_blank"),
					buttonText: "Contact Support",
					message: `${(error as Error)?.message || "There was an error accepting the invite"}`,
					title: "Invite Accept Error",
					tone: "negative",
				});
				return;
			}

			// The invite is already accepted, so a failed profile refresh must not be reported as a failed acceptance.
			try {
				await this.sdk.me();
			} catch (error: unknown) {
				Sentry.captureException(error);
			}
		}
	}

	async checkForDisclosures(addresses: DisclosureAddress[]): Promise<void> {
		if (this.getSessionValue(vIDStateName.IsIdentityRecheck)) {
			return;
		}

		const vIdVoucher = this.getSessionValue(vIDStateName.VidVoucher);
		const voucherId = this.getSessionValue(vIDStateName.VoucherId);
		const beams = this.getSessionValue(vIDStateName.PassTypeBeamsData);

		if (!vIdVoucher) {
			console.error("VidVoucher is not set");
			return;
		}

		const additionalSteps =
			beams?.map((beam) => ({
				id: `${VidStepsEnum.AdditionalInformation}/${beam.id}`,
				label: beam.name,
			})) ?? [];

		let voucherValidity: CheckVoucherValidityQuery["checkVoucherValidity"];
		try {
			const voucherValidityResponse = await this.sdk.CheckVoucherValidity({
				voucherId,
			});
			voucherValidity = voucherValidityResponse.checkVoucherValidity;
		} catch (error) {
			console.error("Error checking voucher validity:", { error, voucherId });
			this.captureVoucherCheckError({ error, voucherId });
			const { rejection } = getVoucherRejection({ error });
			analytics.track(ANALYTICS.userErrorShown, { reason: "voucher_check_failed" satisfies AnalyticsUserErrorReason });
			showDialog({
				autoClose: 30000,
				buttonClick: () => window.open("mailto:support@cerebrum.com", "_blank"),
				buttonText: "Contact Support",
				message:
					rejection === "VOUCHER_NOT_FOUND"
						? VOUCHER_NOT_FOUND_MESSAGE
						: "Error checking voucher validity. Please reload the page or contact support.",
				title: "Oh no!",
				tone: "negative",
			});
			return;
		}

		if (!voucherValidity) {
			const error = new Error("CheckVoucherValidity returned no voucher");
			console.error("Error checking voucher validity:", { error, voucherId });
			this.captureVoucherCheckError({ error, voucherId });
		}

		const sanitizedAddresses = sanitizeAddresses(addresses);
		const response = await this.sdk.RetrieveDisclosures({
			packageId: vIdVoucher.packageSetId,
			organizationId: vIdVoucher.organizationId,
			addresses: sanitizedAddresses,
		});

		const disclosures = response.retrieveDisclosures;

		const latestDisclosures = disclosures ?? [];
		const showDisclosuresStep = Boolean(voucherValidity?.packageSet?.showDisclosures);

		const noDisclosures = !(latestDisclosures?.length > 0);

		const updatedSteps = getVidSteps({
			skipLiveness: Boolean(voucherValidity?.packageSet?.metadata?.idScanOptions?.bypassLivenessDetection),
			additionalSteps,
			collectSSN: Boolean(voucherValidity?.packageSet?.collectSSN),
			showDisclosuresStep,
		});

		// Update the stored disclosures and steps
		this.setSessionValue(vIDStateName.ShowDisclosures, showDisclosuresStep);
		this.setSessionValue(vIDStateName.EmbedVidWeb, Boolean(voucherValidity?.packageSet?.metadata?.embedVidWeb));
		this.setSessionValue(vIDStateName.CompletedVidSteps, (prev: VIDState[vIDStateName.CompletedVidSteps]) => {
			const updatedSteps = { ...prev };
			updatedSteps[VidStepsEnum.SignDisclosures] = noDisclosures && showDisclosuresStep;
			return updatedSteps;
		});
		this.setSessionValue(vIDStateName.VidSteps, updatedSteps);
	}

	/** Configures the current voucher’s journey steps and package feature flags. */
	async checkForDisclosuresAndSsn(vIdVoucher: CheckVoucherValidityQuery["checkVoucherValidity"]) {
		if (!vIdVoucher) {
			console.error("VidVoucher is not set");
			return;
		}

		const isIdentityRecheck = vIdVoucher.packageSet.searchTypes.some(
			(searchType) => searchType.name === SearchTypeEnum.IdentityRecheck,
		);
		// Clear consent and temporary evidence when entering the re-check journey.
		// An existing unfinished re-check session can resume.
		if (isIdentityRecheck && !this.getSessionValue(vIDStateName.IsIdentityRecheck)) {
			this.setSessionValue(vIDStateName.BiometricConsent, RESET);
			this.setSessionValue(vIDStateName.BiometricConsentAcceptanceId, RESET);
			this.setSessionValue(vIDStateName.CompletedVidSteps, RESET);
			this.setSessionValue(vIDStateName.VidWebOrder, RESET);
			this.setSessionValue(vIDStateName.UploadedSelfieId, RESET);
			this.setSessionFileAtom("selfieAtom", null);
		}
		this.setSessionValue(vIDStateName.IsIdentityRecheck, isIdentityRecheck);
		const beams = this.getSessionValue(vIDStateName.PassTypeBeamsData);
		const shouldCollectSSN = vIdVoucher.packageSet.collectSSN;
		const showDisclosuresStep = !isIdentityRecheck && Boolean(vIdVoucher.packageSet?.showDisclosures);
		const allowSampleId = vIdVoucher.packageSet.useSampleId;
		const skipLiveness =
			!isIdentityRecheck && (vIdVoucher.packageSet.metadata?.idScanOptions?.bypassLivenessDetection ?? false);
		const embedVidWeb = vIdVoucher.packageSet.metadata?.embedVidWeb ?? false;

		const collectSSN = !isIdentityRecheck && Boolean(shouldCollectSSN);
		const additionalSteps =
			beams?.map((beam) => ({
				id: `${VidStepsEnum.AdditionalInformation}/${beam.id}`,
				label: beam.name,
			})) ?? [];
		const updatedSteps = getVidSteps({
			isIdentityRecheck,
			skipLiveness,
			additionalSteps,
			collectSSN,
			showDisclosuresStep,
		});

		this.store.set(allowSampleIdAtom, Boolean(allowSampleId));
		this.setSessionValue(vIDStateName.CollectSSN, collectSSN);
		this.setSessionValue(vIDStateName.SkipLiveness, skipLiveness);
		this.setSessionValue(vIDStateName.EmbedVidWeb, embedVidWeb);
		this.setSessionValue(vIDStateName.ShowDisclosures, showDisclosuresStep);
		this.setSessionValue(vIDStateName.VidSteps, updatedSteps);
	}

	decodeInvite(invite: string | string[]): Invite {
		const inviteString = Array.isArray(invite) ? invite.join("") : invite;
		let decodedInvite: Invite = {};
		try {
			const normalizedInviteString = decodeURIComponent(inviteString);
			const jsonString = decodeBase64Utf8(normalizedInviteString);
			const parsedInvite = JSON.parse(jsonString);
			if (typeof parsedInvite === "object" && parsedInvite !== null) {
				decodedInvite = parsedInvite;
			}
		} catch (error) {
			throw error;
		}
		return decodedInvite;
	}

	async processInviteString(invite: string): Promise<void> {
		try {
			const decoded = this.decodeInvite(invite);
			const { isUserAlreadyRegistered, apiKeyLocked, apiKey, email, organization, voucherId } = decoded;

			this.setVoucherReportingIdentity({ email, voucherId });

			if (!email || !voucherId) {
				console.error("INVITE_DECODE_MISSING_FIELDS: Email or voucherId missing from decoded invite", {
					timestamp: new Date().toISOString(),
					email,
					voucherId,
					decodedKeys: Object.keys(decoded),
					fullDecoded: decoded,
				});
				throw new Error("Email or voucherId is not set in the invite string");
			}

			// Partition every invite by voucher before any session writes. API-key and standard flows both need
			// `getVidSessionId()` / hooks to see the same id; tab sessionStorage backs `/vid/welcome` after Cognition login when the URL has no query.
			const vidSessionId = `session_${voucherId}`;
			persistVidPartitionKeyToTabSession(vidSessionId);

			analytics.identify({ email, voucherId });

			this.setSessionValue(vIDStateName.VoucherEmail, email);
			this.setSessionValue(vIDStateName.OrgContactInfo, organization || {});
			this.setSessionValue(vIDStateName.VoucherId, voucherId);

			if (apiKey) {
				const keyDetails: KeyDetails = {
					apiKey,
					apiKeyLocked,
					isUserAlreadyRegistered,
				};
				this.setSessionValue(vIDStateName.KeyDetails, keyDetails);

				void navigateApp({ search: { vidSessionId }, to: paths.vidWelcome });
				return;
			}

			const activeUser = await this.sdk.me();
			const isLoggedIn = activeUser?.me !== null;

			if (isLoggedIn) {
				this.clearVidData();
				const currentUrl = window.location.href;
				void navigateApp({ search: { return_to: currentUrl }, to: paths.logout });
				return;
			}

			const userExistsResponse = await this.sdk.doesUserExist({
				email: email,
			});

			const userExists = userExistsResponse?.doesUserExist;

			const authSearch = { email, return_to: `${window.location.origin}/vid/welcome` };

			if (userExists) {
				void navigateApp({ search: authSearch, to: paths.quickLogin });
				return;
			}

			void navigateApp({ search: authSearch, to: paths.quickRegistration });
		} catch (error) {
			handleDecodeError(error);
		}
	}
}

const manager = new Manager();

export default manager;
